import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import {
  formatDuration,
  plannedAttempts,
  plannedSpanMinutes,
  stepName,
  stepStartOffsets,
  type CampaignSpec,
  type SequenceStep,
} from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { StepCard, type StepContext } from './StepCard';
import { newStep, normaliseTriggers, reorder } from './spec';

/**
 * The campaign as a schedule, which is what a run actually is.
 *
 * A vertical time axis down the left with the audience at the top and every
 * step hung off it in order. The wait before a step rides on the axis rather
 * than living inside the step's form, because "when does this happen" is the
 * question the previous layout could not answer at a glance — it showed a list
 * of forms and left the reader to add up the delays.
 *
 * Offsets are a LOWER BOUND and labelled as one: they assume every step runs,
 * every attempt resolves instantly, and the calling window pushes nothing. The
 * simulator is what answers the same question against a real clock. Being
 * approximate is fine; being silently approximate would not be.
 */

/**
 * `start`, `+4h`, `+28h`, `+3d` — the time axis, in a narrow gutter.
 *
 * Compact rather than prose ("4 hours in") because this is an axis: it is
 * scanned down the left edge, and anything long enough to wrap to two lines
 * stops the rows lining up with the cards they label. The full phrasing lives
 * in the description panel, where there is room for it.
 */
function offsetLabel(minutes: number): string {
  if (minutes <= 0) return 'start';
  if (minutes % (24 * 60) === 0) return `+${minutes / (24 * 60)}d`;
  if (minutes % 60 === 0) return `+${minutes / 60}h`;
  return `+${minutes}m`;
}

/**
 * The gutter the time labels live in, and the indent everything else shares.
 *
 * Exported because the campaign-wide settings below the rail must start on the
 * SAME left edge as the step cards. Aligned to the gutter instead, the two
 * halves of the page read as two unrelated columns — which is most of what made
 * the first cut feel unbalanced.
 */
export const RAIL_GUTTER = '4.5rem';
export const RAIL_CONTENT_INDENT = 'pl-24';

/** One row of the rail: when it happens on the left, what happens on the right. */
function RailRow({
  when,
  last,
  hollow,
  children,
}: {
  when: string;
  /** Stops the connector below the final node. */
  last?: boolean;
  /** An open circle — this node is a wait, not the start of the campaign. */
  hollow?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex">
      {/* `pr-3` keeps the label off the node dot, which sits at the track's left edge. */}
      <div className="w-18 shrink-0 pr-3 pt-3 text-right text-xs tabular-nums text-muted-foreground">
        {when}
      </div>
      {/*
        `min-w-0`: a flex item floors at its content's width, so without it a
        long step summary widened the rail past its column and slid the cards
        under the preview card beside it.
      */}
      <div className="relative min-w-0 flex-1 pb-3 pl-6">
        {!last && (
          <span className="absolute bottom-0 left-[5px] top-4 w-px bg-border" aria-hidden />
        )}
        <span
          className={cn(
            'absolute left-0 top-3.5 h-[11px] w-[11px] rounded-full border-2 bg-background',
            hollow ? 'border-muted-foreground/40' : 'border-primary'
          )}
          aria-hidden
        />
        {children}
      </div>
    </div>
  );
}

interface TimelineProps {
  spec: CampaignSpec;
  onSpecChange: (spec: CampaignSpec) => void;
  /**
   * Which step is expanded — a step id, or nothing. Held by the editor rather
   * than by each step so a click in the text panel can open the one that owns
   * the sentence.
   */
  openId: string;
  onOpenChange: (id: string) => void;
  /** What every step's controls need from the campaign (G). */
  context: StepContext;
}

export function Timeline({
  spec,
  onSpecChange,
  openId,
  onOpenChange,
  context,
}: TimelineProps): React.ReactElement {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so clicking the handle to
    // focus it does not nudge the order.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const offsets = stepStartOffsets(spec);
  const setSequence = (sequence: SequenceStep[]): void => onSpecChange({ ...spec, sequence });

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;
    const from = spec.sequence.findIndex((s) => s.id === active.id);
    const to = spec.sequence.findIndex((s) => s.id === over.id);
    if (from === -1 || to === -1) return;
    setSequence(normaliseTriggers(reorder(spec.sequence, from, to)));
  };

  const remove = (index: number): void => {
    const next = normaliseTriggers(spec.sequence.filter((_, i) => i !== index));
    setSequence(next);
    if (spec.sequence[index]!.id === openId && next.length) {
      onOpenChange(next[Math.min(index, next.length - 1)]!.id);
    }
  };

  const attempts = plannedAttempts(spec);
  const span = plannedSpanMinutes(spec);
  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <div>
          <SortableContext
            items={spec.sequence.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {spec.sequence.map((step, index) => (
              <RailRow
                key={step.id}
                when={offsetLabel(offsets[index]!)}
                hollow={index > 0}
                last={index === spec.sequence.length - 1}
              >
                <StepCard
                  step={step}
                  index={index}
                  isLast={index === spec.sequence.length - 1}
                  previousStepLabel={
                    index > 0 ? stepName(spec.sequence[index - 1]!, index - 1) : undefined
                  }
                  nextStepLabel={
                    index < spec.sequence.length - 1
                      ? stepName(spec.sequence[index + 1]!, index + 1)
                      : undefined
                  }
                  open={step.id === openId}
                  onOpenChange={(open) => onOpenChange(open ? step.id : '')}
                  onChange={(next) =>
                    setSequence(spec.sequence.map((s) => (s.id === next.id ? next : s)))
                  }
                  onDelete={spec.sequence.length > 1 ? () => remove(index) : undefined}
                  context={context}
                />
              </RailRow>
            ))}
          </SortableContext>
        </div>
      </DndContext>

      <div className={RAIL_CONTENT_INDENT}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full border-dashed"
          disabled={spec.sequence.length >= 20}
          onClick={() => {
            const step = newStep(spec.sequence);
            setSequence(normaliseTriggers([...spec.sequence, step]));
            onOpenChange(step.id);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add a step
        </Button>
      </div>

      {/*
        What the sequence actually spends, against what it is allowed. Both
        numbers exist elsewhere in the editor and neither has ever been next to
        the other — which is how a campaign capped at 6 calls that gives up after
        three gets published without anyone noticing.
      */}
      <div className={cn(RAIL_CONTENT_INDENT, 'space-y-2 pt-1 text-xs text-muted-foreground')}>
        <p>
          <span className="font-medium text-foreground tabular-nums">
            {attempts} of {spec.maxTotalAttempts} calls
          </span>{' '}
          used, finishing{' '}
          <span className="font-medium text-foreground">
            {span <= 0 ? 'the moment it starts' : `${formatDuration(span)} in`}
          </span>
          {spec.expiresAfterDays != null && ` of the ${spec.expiresAfterDays} days allowed`}.
          Calling hours push real attempts later than this.
        </p>
      </div>
    </div>
  );
}
